export const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    carousel.on('created', (carousel) => {
      carousel.container.setAttribute(`data-initialised`, true)
    })
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} elements
 */
export const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details

      if (next) {
        next.disabled = abs === maxIdx
        next.setAttribute('aria-hidden', maxIdx === 0)
      }

      if (previous) {
        previous.disabled = abs === min
        previous.setAttribute('aria-hidden', maxIdx === 0)
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
    carousel.on('destroyed', () => {
      next?.removeEventListener('click', carousel.next)
      previous?.removeEventListener('click', carousel.prev)
    })
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
export const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    if (delay === 0) return carousel

    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    const onMouseOver = () => {
      mouseOver = true
      clearCurrentTimout()
    }
    const onMouseOut = () => {
      mouseOver = false
      setNextTimeout()
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', onMouseOver)
        carousel.container.addEventListener('mouseout', onMouseOut)
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
    carousel.on('destroyed', () => {
      clearCurrentTimout()

      carousel.container.removeEventListener('mouseover', onMouseOver)
      carousel.container.removeEventListener('mouseout', onMouseOut)
    })
  }
}

/** @param {HTMLButtonElement[]} dots */
export const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        const isCurrent = carousel.track.details.rel === index

        dot.setAttribute('aria-current', String(isCurrent))
      })
    }

    const onDotClicks = dots.map((dot, index) => {
      const onDotClick = () => {
        carousel.moveToIdx(index)
      }

      dot.addEventListener('click', onDotClick)

      return onDotClick
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
    carousel.on('destroyed', () => {
      dots.forEach((dot, index) => {
        dot.removeEventListener('click', onDotClicks[index])
      })
    })
  }
}

export const addWheelControls = (direction) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let touchTimeout = null
    let wheelActive = false
    let position = {}

    carousel?.container?.style?.setProperty(
      direction === 'horizontal'
        ? 'overscroll-behavior-x'
        : 'overscroll-behavior-y',
      'contain',
    )

    const dispatch = (event, name) => {
      position.x -= event.deltaX
      position.y -= event.deltaY

      carousel.container.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheelStart = (event) => {
      position = {
        x: event.pageX,
        y: event.pageY,
      }

      dispatch(event, 'ksDragStart')
    }

    const wheel = (event) => {
      dispatch(event, 'ksDrag')
    }

    const wheelEnd = (event) => {
      dispatch(event, 'ksDragEnd')
    }

    const canNestedScrollableMove = (event) => {
      const scrollable = event.target.closest('[data-keen-slider-scrollable]')

      if (!scrollable) return false

      if (direction === 'horizontal') {
        const maxScroll = scrollable.scrollWidth - scrollable.clientWidth

        return (
          (event.deltaX < 0 && scrollable.scrollLeft > 0) ||
          (event.deltaX > 0 && scrollable.scrollLeft < maxScroll)
        )
      }

      const maxScroll = scrollable.scrollHeight - scrollable.clientHeight

      return (
        (event.deltaY < 0 && scrollable.scrollTop > 0) ||
        (event.deltaY > 0 && scrollable.scrollTop < maxScroll)
      )
    }

    const eventWheel = (event) => {
      if (canNestedScrollableMove(event)) return

      let supportWheel = true
      let earlyReturn = false

      const { details } = carousel.track

      /* Ensure we don't block vertical scrolling for horizontal carousels */
      if (
        direction === 'horizontal' &&
        (event.deltaY > 1 || event.deltaY < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Ensure we don't block horizontal scrolling for vertical carousels */
      if (
        !earlyReturn &&
        direction === 'vertical' &&
        (event.deltaX > 1 || event.deltaX < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Prevent overscrolling for non-looping carousels */
      if (!earlyReturn && !carousel.options.loop) {
        const scrollIndex =
          direction === 'horizontal' ? event.deltaX : event.deltaY

        if (
          (details.progress <= 0 && scrollIndex < 0) ||
          (details.position >= details.max && scrollIndex > 0)
        ) {
          earlyReturn = true
        }
      }

      /* Prevent default scroll functionality for carousels supporting wheel and scrolling on the correct plane */
      if (supportWheel) {
        event.preventDefault()
      }

      if (earlyReturn) {
        if (touchTimeout && wheelActive) {
          clearTimeout(touchTimeout)

          wheelActive = false

          wheelEnd(event)
        }

        return
      }

      if (!wheelActive) {
        wheelStart(event)
        wheelActive = true
      }

      wheel(event)

      clearTimeout(touchTimeout)

      touchTimeout = setTimeout(() => {
        wheelActive = false

        wheelEnd(event)
      }, 150)
    }

    carousel.on('created', () => {
      carousel.container.addEventListener('wheel', eventWheel, {
        passive: false,
      })
    })
    carousel.on('destroyed', () => {
      clearTimeout(touchTimeout)

      carousel.container.removeEventListener('wheel', eventWheel)
    })
  }
}
